import React, { useState, Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { TrashIcon, QrCodeIcon, MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import ModernProductSelector from "../../../utils/Selector/ModernProductSelector";
import { fetchProducts } from "../../../../api/product";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

// Helper functions from order-items.js
const formatPriceInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value);
  // Remove all characters except numbers, comma, and dot
  const cleaned = stringValue.replace(/[^0-9,.]/g, "");
  // Replace dot with comma for decimal separator
  return cleaned.replace(/\./g, ",");
};

// Helper function to format price with exactly two decimal places
const formatPriceWithTwoDecimals = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value).trim();
  
  // If empty after trimming, return empty string
  if (stringValue === "") return "";
  
  // If value contains a comma, ensure it has exactly two decimal places
  if (stringValue.includes(",")) {
    const parts = stringValue.split(",");
    const integerPart = parts[0] || "";
    const decimalPart = parts[1] || "";
    
    // Pad decimal part to 2 digits or truncate if more than 2
    let formattedDecimal = decimalPart.substring(0, 2);
    if (formattedDecimal.length === 1) {
      // If only one digit, pad with zero: "21,2" -> "21,20"
      formattedDecimal = formattedDecimal + "0";
    } else if (formattedDecimal.length === 0) {
      // If no digits after comma, add "00": "21," -> "21,00"
      formattedDecimal = "00";
    }
    
    return `${integerPart},${formattedDecimal}`;
  }
  
  // If no comma and value is a valid number, add ",00": "100" -> "100,00"
  if (/^\d+$/.test(stringValue)) {
    return `${stringValue},00`;
  }
  
  // Otherwise return as is
  return stringValue;
};

const parseNumericValue = (value) => {
  if (!value || value === "") return 0;
  const stringValue = String(value);
  const normalized = stringValue.replace(/,/g, ".");
  return parseFloat(normalized) || 0;
};

const ReportAddItemsDialog = ({ open, onClose, onSave, report, onSubDialogStateChange }) => {
  const [items, setItems] = useState([
    {
      id: uuidv4(),
      product_id: null,
      product: null,
      articleNumber: "",
      description: "",
      quantity: "",
      unit_id: null,
      unit: null,
      sales_price: "",
      purchase_price: "",
      discount: "",
      surcharge: "",
    },
  ]);
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [selectedItemRow, setSelectedItemRow] = useState(null);
  const [qrScanMode, setQrScanMode] = useState(false);
  const [qrCodeInput, setQrCodeInput] = useState("");

  // Track if product selector is open and notify parent
  useEffect(() => {
    if (onSubDialogStateChange) {
      onSubDialogStateChange(productSelectorOpen);
    }
  }, [productSelectorOpen, onSubDialogStateChange]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setItems([
        {
          id: uuidv4(),
          product_id: null,
          product: null,
          articleNumber: "",
          description: "",
          quantity: "",
          unit_id: null,
          unit: null,
          sales_price: "",
          purchase_price: "",
          discount: "",
          surcharge: "",
        },
      ]);
      setQrScanMode(false);
      setQrCodeInput("");
      setSelectedItemRow(null);
      setProductSelectorOpen(false);
    }
  }, [open]);

  // Auto-add new row when any row is filled
  useEffect(() => {
    if (!open) return;

    // Check if any row has been filled (has product OR description, and quantity)
    const hasFilledRow = items.some((item) => {
      const hasProduct = item.product_id || item.product;
      const hasDescription = item.description && item.description.trim() !== "";
      const hasQuantity = item.quantity && parseFloat(item.quantity) > 0;
      return (hasProduct || hasDescription) && hasQuantity;
    });

    // If we have a filled row, check if we need to add a new empty row
    if (hasFilledRow && items.length > 0) {
      const hasEmptyRow = items.some(
        (item) =>
          (!item.product_id && !item.product && (!item.description || item.description.trim() === "")) ||
          (!item.quantity || parseFloat(item.quantity) === 0)
      );

      // If no empty row exists, add one
      if (!hasEmptyRow) {
        setItems((prevItems) => [
          ...prevItems,
          {
            id: uuidv4(),
            product_id: null,
            product: null,
            articleNumber: "",
            description: "",
            quantity: "",
            unit_id: null,
            unit: null,
            sales_price: "",
            purchase_price: "",
            discount: "",
            surcharge: "",
          },
        ]);
      }
    }
  }, [items, open]);

  const handleRowProductSelect = (item) => {
    setSelectedItemRow(item);
    setProductSelectorOpen(true);
  };

  const handleProductSelect = (products) => {
    if (!selectedItemRow || !products || products.length === 0) {
      setProductSelectorOpen(false);
      setSelectedItemRow(null);
      return;
    }

    const selectedProduct = products[0];
    setItems(
      items.map((item) =>
        item.id === selectedItemRow.id
          ? {
              ...item,
              product_id: selectedProduct.id,
              product: selectedProduct,
              articleNumber: selectedProduct.item_number || "",
              unit_id: selectedProduct.unit?.id || selectedProduct.unit_id,
              unit: selectedProduct.unit,
              sales_price: selectedProduct.sales_price || "",
              purchase_price: selectedProduct.purchase_price || "",
              quantity: item.quantity || 1,
            }
          : item
      )
    );

    setProductSelectorOpen(false);
    setSelectedItemRow(null);
  };

  const handleArticleNumberSearch = async (item, articleNumber) => {
    if (!articleNumber || articleNumber.trim() === "") return;

    try {
      const response = await fetchProducts({
        query: articleNumber.trim(),
        page: 1,
        per_page: 10,
        fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
      });

      if (response && response.data && response.data.length > 0) {
        // Check for exact match on item_number
        const exactMatch = response.data.find(
          (p) =>
            p.item_number &&
            p.item_number.toLowerCase() === articleNumber.trim().toLowerCase()
        );

        if (exactMatch) {
          setItems(
            items.map((i) =>
              i.id === item.id
                ? {
                    ...i,
                    product_id: exactMatch.id,
                    product: exactMatch,
                    articleNumber: articleNumber.trim(),
                    unit_id: exactMatch.unit?.id || exactMatch.unit_id,
                    unit: exactMatch.unit,
                    sales_price: exactMatch.sales_price || "",
                    purchase_price: exactMatch.purchase_price || "",
                    quantity: i.quantity || 1,
                  }
                : i
            )
          );
          toast.success("Produkt funnet og lagt til");
        } else {
          // Open product selector if no exact match
          setSelectedItemRow(item);
          setProductSelectorOpen(true);
          toast.info("Ingen eksakt match funnet. Velg produkt fra listen.");
        }
      } else {
        toast.error("Ingen produkt funnet");
      }
    } catch (error) {
      console.error("Error searching product:", error);
      toast.error("Kunne ikke søke etter produkt");
    }
  };

  const handleQrCodeScan = async (scannedCode) => {
    if (!scannedCode || scannedCode.trim() === "") {
      toast.error("Skriv inn eller skann en QR-kode");
      return;
    }

    // Find first empty row or create new one
    let targetItem = items.find(
      (item) => !item.product_id && !item.product
    );
    if (!targetItem) {
      const newItem = {
        id: uuidv4(),
        product_id: null,
        product: null,
        articleNumber: "",
        description: "",
        quantity: "",
        unit_id: null,
        unit: null,
        sales_price: "",
        purchase_price: "",
        discount: "",
        surcharge: "",
      };
      setItems([...items, newItem]);
      targetItem = newItem;
    }

    await handleArticleNumberSearch(targetItem, scannedCode.trim());
    setQrCodeInput("");
  };

  const handleItemChange = (itemId, field, value) => {
    setItems(
      items.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleRemoveRow = (itemId) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== itemId));
    }
  };

  const handleSave = async () => {
    // Filter out empty rows - allow items with either:
    // 1. Product (product_id or product object)
    // 2. Description + quantity (manual item)
    const validItems = items.filter((item) => {
      const hasProduct = item.product_id || item.product;
      const hasDescription = item.description && item.description.trim() !== "";
      const hasQuantity = item.quantity && parseFloat(item.quantity) > 0;
      
      // Valid if: (has product OR has description) AND has quantity
      return (hasProduct || hasDescription) && hasQuantity;
    });

    if (validItems.length === 0) {
      toast.error("Ingen gyldige produkter å legge til. Velg produkt eller fyll ut beskrivelse, og legg til antall.");
      return;
    }

    const itemsToAdd = validItems.map((item) => {
      // If product exists, use product data
      if (item.product_id || item.product) {
        return {
          id: null,
          product_id: item.product_id,
          product: item.product,
          title: item.product?.displayTitle || item.product?.title,
          description: item.description || "",
          quantity: parseFloat(item.quantity) || 1,
          unit_id: item.unit_id || item.product?.unit?.id,
          unit: item.unit || item.product?.unit,
          sales_price: item.sales_price
            ? parseNumericValue(item.sales_price)
            : item.product?.sales_price || 0,
          purchase_price: item.purchase_price
            ? parseNumericValue(item.purchase_price)
            : item.product?.purchase_price || 0,
          discount: item.discount ? parseNumericValue(item.discount) : 0,
          surcharge: item.surcharge ? parseNumericValue(item.surcharge) : 0,
          picked: false,
          taken_in_vehicle: false,
          returned_to_storage: false,
          left_in_vehicle: false,
          storage_id: null,
          invoiced: false,
        };
      } else {
        // Manual item with description only
        return {
          id: null,
          product_id: null,
          product: null,
          description: item.description || "",
          quantity: parseFloat(item.quantity) || 1,
          unit_id: null,
          unit: null,
          sales_price: item.sales_price ? parseNumericValue(item.sales_price) : 0,
          purchase_price: item.purchase_price ? parseNumericValue(item.purchase_price) : 0,
          discount: item.discount ? parseNumericValue(item.discount) : 0,
          surcharge: item.surcharge ? parseNumericValue(item.surcharge) : 0,
          picked: false,
          taken_in_vehicle: false,
          returned_to_storage: false,
          left_in_vehicle: false,
          storage_id: null,
          invoiced: false,
        };
      }
    });

    await onSave(itemsToAdd);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog 
          as="div" 
          className="relative z-[2100]" 
          onClose={(e) => {
            // Only close if product selector is not open
            if (!productSelectorOpen) {
              onClose();
            }
          }} 
          static
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6">
                  <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                    Legg til produkter
                  </Dialog.Title>

                  {/* Action buttons */}
                  <div className="mb-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => setQrScanMode(!qrScanMode)}
                    >
                      <QrCodeIcon className="h-5 w-5 mr-2" />
                      Skann QR-kode
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => {
                        // Find first empty row or create new one
                        let targetItem = items.find(
                          (item) => !item.product_id && !item.product
                        );
                        if (!targetItem) {
                          const newItem = {
                            id: uuidv4(),
                            product_id: null,
                            product: null,
                            articleNumber: "",
                            description: "",
                            quantity: "",
                            unit_id: null,
                            unit: null,
                            sales_price: "",
                            purchase_price: "",
                            discount: "",
                            surcharge: "",
                          };
                          setItems([...items, newItem]);
                          targetItem = newItem;
                        }
                        setSelectedItemRow(targetItem);
                        setProductSelectorOpen(true);
                      }}
                    >
                      <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                      Søk manuelt
                    </button>
                  </div>

                  {/* QR Code Input */}
                  {qrScanMode && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="Skann QR-kode eller skriv produktnummer"
                          value={qrCodeInput}
                          onChange={(e) => setQrCodeInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleQrCodeScan(qrCodeInput);
                            }
                          }}
                          autoFocus
                        />
                        <button
                          type="button"
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          onClick={() => handleQrCodeScan(qrCodeInput)}
                        >
                          Søk
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          onClick={() => {
                            setQrScanMode(false);
                            setQrCodeInput("");
                          }}
                        >
                          Lukk
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Table */}
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                            Produkt
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                            Beskrivelse
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[100px]">
                            Antall/Enhet
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[120px]">
                            Innkjøpspris
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[120px]">
                            Salgspris
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[100px]">
                            Rabatt (%)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[100px]">
                            Påslag (%)
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[50px]">
                            Handlinger
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {items.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            {/* Product Selection */}
                            <td className="px-4 py-3 whitespace-nowrap border-r border-gray-200">
                              <div className="space-y-2">
                                <div
                                  className={`cursor-pointer hover:bg-blue-50 p-2 rounded border-2 border-dashed transition-colors ${
                                    item.product
                                      ? "border-blue-300 bg-blue-50"
                                      : "border-gray-300"
                                  }`}
                                  onClick={() => handleRowProductSelect(item)}
                                >
                                  {item.product ? (
                                    <div>
                                      <div className="font-bold text-blue-700">
                                        {item.product.displayTitle ||
                                          item.product.title}
                                      </div>
                                      {item.product.item_number && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          Art.nr: {item.product.item_number}
                                        </div>
                                      )}
                                      {item.description && (
                                        <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
                                          {item.description}
                                        </div>
                                      )}
                                    </div>
                                  ) : item.description ? (
                                    <div>
                                      <div className="font-bold text-blue-700 whitespace-pre-wrap">
                                        {item.description}
                                      </div>
                                      <div className="text-xs text-gray-500 italic mt-1">
                                        Manuell vare
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-gray-500 text-center">
                                      <div className="text-sm font-medium">
                                        + Klikk for å velge produkt
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <input
                                  type="text"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Artikkelnr"
                                  value={item.articleNumber}
                                  onChange={(e) => {
                                    handleItemChange(
                                      item.id,
                                      "articleNumber",
                                      e.target.value
                                    );
                                  }}
                                  onBlur={() => {
                                    if (item.articleNumber) {
                                      handleArticleNumberSearch(
                                        item,
                                        item.articleNumber
                                      );
                                    }
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter" && item.articleNumber) {
                                      handleArticleNumberSearch(
                                        item,
                                        item.articleNumber
                                      );
                                    }
                                  }}
                                />
                              </div>
                            </td>

                            {/* Description */}
                            <td className="px-4 py-3 border-r border-gray-200">
                              <input
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                value={item.description || ""}
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "description",
                                    e.target.value
                                  )
                                }
                                placeholder={!item.product_id && !item.product ? "Beskrivelse (manuell vare)" : "Beskrivelse"}
                              />
                            </td>

                            {/* Quantity/Unit */}
                            <td className="px-4 py-3 whitespace-nowrap text-center border-r border-gray-200">
                              <div className="flex items-center justify-center space-x-1">
                                <input
                                  type="number"
                                  className="w-16 px-2 py-1 text-sm text-center border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleItemChange(
                                      item.id,
                                      "quantity",
                                      e.target.value
                                    )
                                  }
                                  min="0.01"
                                  step="0.01"
                                />
                                <span className="text-xs text-gray-600 font-medium">
                                  {item.unit?.title || "-"}
                                </span>
                              </div>
                            </td>

                            {/* Purchase Price */}
                            <td className="px-4 py-3 whitespace-nowrap border-r border-gray-200">
                              <input
                                type="text"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={
                                  item.purchase_price
                                    ? formatPriceInput(item.purchase_price)
                                    : ""
                                }
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "purchase_price",
                                    formatPriceInput(e.target.value)
                                  )
                                }
                                onBlur={(e) => {
                                  // Format with two decimals on blur
                                  if (e.target.value && e.target.value.trim() !== "") {
                                    const formatted = formatPriceWithTwoDecimals(e.target.value);
                                    handleItemChange(item.id, "purchase_price", formatted);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Only allow numbers, comma, and dot (dot will be converted to comma)
                                  const allowedKeys = /[0-9,.]/;
                                  const isNavigationKey = 
                                    e.key === "Backspace" || 
                                    e.key === "Delete" || 
                                    e.key === "ArrowLeft" || 
                                    e.key === "ArrowRight" || 
                                    e.key === "Tab" ||
                                    e.key === "Enter";
                                  
                                  if (!allowedKeys.test(e.key) && !isNavigationKey) {
                                    e.preventDefault();
                                  }
                                  // Convert dot to comma immediately
                                  if (e.key === ".") {
                                    e.preventDefault();
                                    const syntheticValue = (item.purchase_price || "") + ",";
                                    handleItemChange(item.id, "purchase_price", syntheticValue);
                                  }
                                }}
                                placeholder="0,00"
                              />
                            </td>

                            {/* Sales Price */}
                            <td className="px-4 py-3 whitespace-nowrap border-r border-gray-200">
                              <input
                                type="text"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={
                                  item.sales_price
                                    ? formatPriceInput(item.sales_price)
                                    : ""
                                }
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "sales_price",
                                    formatPriceInput(e.target.value)
                                  )
                                }
                                onBlur={(e) => {
                                  // Format with two decimals on blur
                                  if (e.target.value && e.target.value.trim() !== "") {
                                    const formatted = formatPriceWithTwoDecimals(e.target.value);
                                    handleItemChange(item.id, "sales_price", formatted);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Only allow numbers, comma, and dot (dot will be converted to comma)
                                  const allowedKeys = /[0-9,.]/;
                                  const isNavigationKey = 
                                    e.key === "Backspace" || 
                                    e.key === "Delete" || 
                                    e.key === "ArrowLeft" || 
                                    e.key === "ArrowRight" || 
                                    e.key === "Tab" ||
                                    e.key === "Enter";
                                  
                                  if (!allowedKeys.test(e.key) && !isNavigationKey) {
                                    e.preventDefault();
                                  }
                                  // Convert dot to comma immediately
                                  if (e.key === ".") {
                                    e.preventDefault();
                                    const syntheticValue = (item.sales_price || "") + ",";
                                    handleItemChange(item.id, "sales_price", syntheticValue);
                                  }
                                }}
                                placeholder="0,00"
                              />
                            </td>

                            {/* Discount */}
                            <td className="px-4 py-3 whitespace-nowrap border-r border-gray-200">
                              <input
                                type="number"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={item.discount}
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "discount",
                                    e.target.value
                                  )
                                }
                                placeholder="0"
                              />
                            </td>

                            {/* Surcharge */}
                            <td className="px-4 py-3 whitespace-nowrap border-r border-gray-200">
                              <input
                                type="number"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={item.surcharge}
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "surcharge",
                                    e.target.value
                                  )
                                }
                                placeholder="0"
                              />
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              {items.length > 1 && (
                                <button
                                  type="button"
                                  className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                  onClick={() => handleRemoveRow(item.id)}
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={onClose}
                    >
                      Avbryt
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={handleSave}
                    >
                      Legg til produkter
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Product Selector Dialog */}
      <Transition.Root show={productSelectorOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[2200]"
          onClose={(e) => {
            // Prevent closing the parent dialog
            e?.stopPropagation?.();
            setProductSelectorOpen(false);
            setSelectedItemRow(null);
          }}
          static
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                  <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                    Velg produkt
                  </Dialog.Title>
                  <div className="h-[500px] border border-gray-200 rounded-lg overflow-hidden">
                    <ModernProductSelector
                      open={true}
                      selectedProducts={
                        selectedItemRow?.product
                          ? [selectedItemRow.product]
                          : []
                      }
                      onChange={handleProductSelect}
                      onClose={() => {
                        setProductSelectorOpen(false);
                        setSelectedItemRow(null);
                      }}
                      multiple={false}
                      filters={[]}
                      showHeader={false}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ReportAddItemsDialog;
